'use strict'

/**
 * Manifest + settlement math for upstream master lots.
 *
 * Answers: "what are the lot's totals, weighted purities, and
 * contract-driven settlement preview/totals/SLA?"
 *
 * Sections (top → bottom):
 *   - METAL_KEYS + weighted-avg projection
 *   - manifest totals + weighted-average-purity primitives
 *   - summary snapshot (persisted-lot projection)
 *   - manifest-for-variance projection
 *   - per-unit purity unit-of-measure normalization (fraction ↔ percent)
 *   - tier resolution (tier match + cross-metal qualifier)
 *   - settlement preview (per-metal credited-fine-toz + refining-fee)
 *   - settlement-time impurity-charge predicate (used by SLA dispatch)
 *   - SLA timing (max business days + modifier dispatch)
 *   - settlement totals (lot-level USD aggregates)
 *
 * "Unit" definition: anything contributing to a master lot manifest — a
 * bar or pin picked from a settled refinery lot (kind `'bar'` /
 * `'house_pin'` / `'customer_pin'`), or a catalog SKU line (kind
 * `'catalog'`). Every unit carries:
 *
 *   - source/bar/pin: rich `weight` (`{troyOunces, …}`); catalog: rich
 *     `unitWeight` + scalar line-total `weightToz`.
 *   - `purity: { [metal]: number }` — per-metal purity (percent, 0-100)
 *     for any key in `METAL_KEYS` (XRF-all ceiling). Missing entries are
 *     treated as 0 for that metal.
 *
 * Weighted average purity for a metal:
 *
 *     P_avg = sum(W_i * P_i) / sum(W_i)
 */

import {toFinite} from './_shared'
import {manifestUnitGrossToz} from './manifestWeights'
import {ALLOWED_PAID_METALS, resolveFeeAmount} from '../upstreamContract'
import {catalogEntriesFromManifest} from './manifestCatalog'

// ============================================================================
// METAL_KEYS + weighted-avg projection
// ============================================================================

/** Upstream metal vocabulary / METAL_KEYS ← XRF-all. */
export const METAL_KEYS = ALLOWED_PAID_METALS

/**
 * Project a paid-metal symbol onto the flat `weightedAvgPurity{Metal}` key
 * used on persisted `MasterLot.summary` snapshots (e.g. `'au'` →
 * `'weightedAvgPurityAu'`). Lives alongside `METAL_KEYS` + the snapshot
 * builder so the convention has a single source of truth — the read-side
 * card and the write-side dialog both project through this helper.
 */
export const weightedAvgPurityKeyFor = (metal) => {
  const lowered = String(metal).toLowerCase()
  return `weightedAvgPurity${lowered.charAt(0).toUpperCase()}${lowered.slice(1)}`
}

// ============================================================================
// Manifest totals + weighted-average-purity primitives
// ============================================================================

const unitWeight = (unit) => manifestUnitGrossToz(unit)
const unitCount = (unit) => {
  const quantity = Math.floor(Number(unit?.quantity))
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1
}
const manifestUnitCount = (units) => (
  Array.isArray(units) ? units.reduce((sum, unit) => sum + unitCount(unit), 0) : 0
)

const unitPurity = (unit, metal) => {
  const purityValue = unit?.purity?.[metal]
  return Number.isFinite(purityValue) ? purityValue : 0
}

/** Total gross weight (troy ounces) across the supplied units. */
export const totalGrossWeightToz = (units) => {
  if (!Array.isArray(units)) return 0
  return units.reduce((sum, u) => sum + unitWeight(u), 0)
}

/**
 * Weighted average purity for a given metal, expressed as a percent (0-100).
 * Units missing a purity for the metal contribute 0% for that metal.
 * Returns null when the contributing weight is zero.
 */
export const weightedAveragePurity = (units, metal = 'au') => {
  if (!Array.isArray(units) || units.length === 0) return null
  const metalKey = String(metal).toLowerCase()

  let weightedSum = 0
  let totalWeight = 0
  for (const unit of units) {
    const weight = unitWeight(unit)
    if (weight <= 0) continue
    weightedSum += weight * unitPurity(unit, metalKey)
    totalWeight += weight
  }
  if (totalWeight <= 0) return null
  return weightedSum / totalWeight
}

/**
 * Net payable troy ounces for a metal: sum(W_i * P_i / 100).
 * Returns 0 if no contribution.
 */
export const netPayableToz = (units, metal = 'au') => {
  if (!Array.isArray(units) || units.length === 0) return 0
  const key = String(metal).toLowerCase()
  return units.reduce((sum, u) => sum + (unitWeight(u) * unitPurity(u, key)) / 100, 0)
}

/**
 * Compatibility gate for legacy `minimumGrossToz` snapshots.
 * Structured contract requirements are evaluated by readiness.
 */
export const meetsContractMinimum = (units, contract) => {
  if (!contract || !Number.isFinite(contract.minimumGrossToz)) return true
  return totalGrossWeightToz(units) >= contract.minimumGrossToz
}

/**
 * Summarise totals across all metals carried by the units.
 * Returns an object keyed by metal symbol (e.g. 'au', 'ag') with weighted
 * average purity and net payable troy ounces. Metals with no contribution
 * are omitted so the manifest UI does not advertise zero rows.
 */
export const summariseManifest = (units) => {
  const grossToz = totalGrossWeightToz(units)
  const byMetal = {}
  for (const metal of METAL_KEYS) {
    const payable = netPayableToz(units, metal)
    if (payable <= 0) continue
    byMetal[metal] = {
      weightedAvgPurityPct: weightedAveragePurity(units, metal),
      netPayableToz: payable,
    }
  }
  return {
    unitCount: manifestUnitCount(units),
    grossWeightToz: grossToz,
    byMetal,
  }
}

const roundSummaryPurityPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return null
  return Math.round(value * 1000) / 1000
}

/**
 * Weighted-avg purity for a metal when it contributes net payable weight;
 * null when the metal has no manifest contribution.
 */
export const weightedAvgPurityIfContributing = (units, metal) => {
  if (netPayableToz(units, metal) <= 0) return null
  return roundSummaryPurityPct(weightedAveragePurity(units, metal))
}

const roundSummaryGrossToz = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return 0
  return Math.round(value * 1000) / 1000
}

/**
 * Flat `summary` object for persisted master lots (create / composition update).
 * Projects every `METAL_KEYS` entry through `weightedAvgPurity{Metal}`.
 *
 * @param {object} data
 * @param {Array} data.allUnits
 * @param {number} data.sourceUnitCount
 * @param {number} data.catalogEntryCount
 * @param {number} data.sourceLotCount
 */
export const buildMasterLotSummarySnapshot = ({
  allUnits,
  sourceUnitCount = 0,
  catalogEntryCount = 0,
  sourceLotCount = 0,
}) => {
  const units = Array.isArray(allUnits) ? allUnits : []
  const snapshot = {
    unitCount: manifestUnitCount(units),
    sourceUnitCount,
    catalogEntryCount,
    sourceLotCount,
    grossWeightToz: roundSummaryGrossToz(totalGrossWeightToz(units)),
  }
  for (const metal of METAL_KEYS) {
    snapshot[weightedAvgPurityKeyFor(metal)] = weightedAvgPurityIfContributing(units, metal)
  }
  return snapshot
}

const allManifestUnits = (masterLot) => [
  ...(masterLot?.manifest?.sourceUnits || []),
  ...catalogEntriesFromManifest(masterLot?.manifest),
]

/**
 * Project a persisted master lot into the manifest-summary shape
 * `computeSettlementPreview` consumes for variance calculations:
 *
 *   { grossWeightToz, byMetal: { [metal]: { weightedAvgPurityPct } } }
 *
 * Prefers the persisted `summary` flat fields (source of truth on saved
 * lots — `weightedAvgPurity{Metal}` keys + `grossWeightToz`); falls back to
 * recomputing from the manifest's source units + catalog entries when a
 * field is missing (legacy persisted lots that pre-date the XRF-all
 * summary snapshot). Skips metals with no contributing weighted-avg purity.
 *
 * Single home for the projection so the settlement dialog (write-side
 * preview) and `validateSettlementInput` (mock-api write-side validation)
 * can't drift on shape or which metals appear.
 */
export const buildManifestSummaryForVariance = (masterLot) => {
  if (!masterLot) return {grossWeightToz: 0, byMetal: {}}
  const summary = masterLot.summary || {}
  const units = allManifestUnits(masterLot)
  const grossWeightToz = Number.isFinite(summary.grossWeightToz)
    ? summary.grossWeightToz
    : totalGrossWeightToz(units)
  const byMetal = {}
  for (const metal of METAL_KEYS) {
    const summaryValue = summary[weightedAvgPurityKeyFor(metal)]
    if (typeof summaryValue === 'number' && summaryValue > 0) {
      byMetal[metal] = {weightedAvgPurityPct: summaryValue}
    } else if (netPayableToz(units, metal) > 0) {
      byMetal[metal] = {weightedAvgPurityPct: weightedAveragePurity(units, metal)}
    }
  }
  return {grossWeightToz, byMetal}
}

// ============================================================================
// Per-unit purity unit-of-measure normalization (fraction ↔ percent)
// ----------------------------------------------------------------------------
// Convention in the master-lot builder pipeline (math, manifest, catalog entry,
// fixtures): purity is a *percent* in [0, 100]. The DB / refinery backend
// emits per-bar purity as a *fraction* in [0, 1] (`DECIMAL(5,4)`). All
// fraction→percent crossings should go through these helpers so we have one
// place to look when the convention shifts.
// ============================================================================

/**
 * Translate a single fractional purity (0–1) into the master-lot builder's
 * percent form (0–100). Null/undefined/non-finite values pass through as null
 * so caller code can keep treating "no purity for this metal" as null rather
 * than 0.
 */
export const fractionToPercent = (value) => {
  if (value === null || value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n * 100 : null
}

/**
 * Normalize a single picker `unit` from the BE wire shape (fractional purity)
 * to the FE master-lot builder shape (percent purity). Pure: returns a new
 * object — never mutates input.
 */
export const normalizeUnitPurity = (unit) => {
  if (!unit || !unit.purity) return unit
  const purity = {}
  for (const metal of METAL_KEYS) {
    if (Object.prototype.hasOwnProperty.call(unit.purity, metal)) {
      purity[metal] = fractionToPercent(unit.purity[metal])
    }
  }
  return {...unit, purity}
}

/**
 * Normalize a whole upstream source lot, recursively applying
 * `normalizeUnitPurity` to its `units`. Pure: returns a new object.
 */
export const normalizeUpstreamSourceLotPurity = (lot) => {
  if (!lot || !Array.isArray(lot.units)) return lot
  return {
    ...lot,
    units: lot.units.map(normalizeUnitPurity),
  }
}

// ============================================================================
// Tier resolution
// ----------------------------------------------------------------------------
// Upstream settlements are pure cash — no metal balance, no spot chain.
// Tier matching is half-open '[tierMin, tierMax)'; 'tierMax: null' is
// unbounded. When 'contract.tierQualifyingMetal' is set with
// 'tierDimension === purityPct', tier resolution qualifies on the
// cross-metal purity (single contract-wide qualifier).
// ============================================================================

const contractTierDimension = (contract) => (
  contract && contract.tierDimension === 'purityPct' ? 'purityPct' : 'weightToz'
)

/**
 * Pull the matching 'metalRates' row off a contract for a given metal.
 * Half-open '[tierMin, tierMax)'; 'tierMax: null' is unbounded above.
 * Cross-metal qualifier ('contract.tierQualifyingMetal') re-routes purity
 * lookups via 'qualifiers.purityPctByMetal'. Returns null when metal absent
 * or no window matches.
 */
export const resolveMetalRateTier = (contract, metal, qualifiers = {}) => {
  const metalRateRows = contract && Array.isArray(contract.metalRates) ? contract.metalRates : null
  if (!metalRateRows) return null
  const dimension = contractTierDimension(contract)
  const metalKey = String(metal).toLowerCase()
  for (const row of metalRateRows) {
    if (!row || String(row.metalType).toLowerCase() !== metalKey) continue
    const qualifyValue = resolveTierQualifyValue(contract, row, dimension, qualifiers)
    if (!Number.isFinite(qualifyValue)) continue
    const min = Number.isFinite(row.tierMin) ? row.tierMin : 0
    const max = Number.isFinite(row.tierMax) ? row.tierMax : Infinity
    if (qualifyValue >= min && qualifyValue < max) return row
  }
  return null
}

const resolveTierQualifyValue = (contract, row, dimension, qualifiers) => {
  if (dimension === 'weightToz') return qualifiers && qualifiers.grossToz
  if (!qualifiers) return undefined
  const crossQualifier = row?.tierQualifyingMetal || contract?.tierQualifyingMetal
  if (crossQualifier) {
    const byMetal = qualifiers.purityPctByMetal || {}
    const key = String(crossQualifier).toLowerCase()
    if (Object.prototype.hasOwnProperty.call(byMetal, key)) return byMetal[key]
  }
  return qualifiers.purityPct
}

// ============================================================================
// Settlement preview (per-metal credited-fine-toz + refining-fee)
// ============================================================================

/**
 * Given a contract + manifest summary + user-entered settlement inputs,
 * return a per-metal map keyed by every metal in `contract.paidMetals` with:
 *
 *   - purityPct                — pass-through (null when not supplied)
 *   - creditedFineToz          — gross × purity/100 × accountability/100 (null if any input missing)
 *   - refiningFeeUsd           — `refiningFeeMultiplier × tier.fee` (null if tier missing)
 *   - refiningFeeBasis         — `'creditedFineToz' | 'netWeightReceivedToz' | null` per AC J
 *   - refiningFeeMultiplier    — the toz value actually multiplied against `tier.fee`
 *                                (creditedFineToz for default-basis rows; reportedGrossToz
 *                                for `feeBasis: 'netWeightReceivedToz'` rows). null when
 *                                no tier matches.
 *   - metalValueUsd            — pass-through (null when not supplied)
 *   - varianceVsManifestPct    — purityPct − weightedAveragePurity(manifest, metal)
 *
 * Pure: re-runnable on every keystroke. Returns null fields rather than NaN
 * when an input is missing so the dialog/card can render `—` cleanly.
 *
 * Per-tier `feeBasis` controls the multiplier used for the refining-fee
 * calc. Default ('creditedFineToz') preserves baseline bit-for-bit
 * (rate × credited-fine-toz, per-metal). 'netWeightReceivedToz' multiplies
 * against the lot's reported gross weight (Asahi-gold Ag/Pt/Pd rows: partner
 * bills against the lot's total received weight, not per-metal contribution).
 * Basis metadata is threaded through to per-metal output so the read-side
 * settlement card renders the itemized line without re-walking `metalRates`.
 */
export const computeSettlementPreview = (contract, manifestSummary, inputs) => {
  const paidMetals = Array.isArray(contract?.paidMetals) ? contract.paidMetals : []
  const reportedGrossToz = Number.isFinite(inputs?.reportedGrossToz) ? inputs.reportedGrossToz : null
  const perMetalInputs = (inputs && inputs.perMetal) || {}
  const byMetal = (manifestSummary && manifestSummary.byMetal) || {}
  // Thread purityPctByMetal so cross-metal-qualified contracts resolve tier rows against the qualifying metal's purity.
  const purityPctByMetal = {}
  for (const metal of paidMetals) {
    const key = String(metal).toLowerCase()
    const inputForMetal = perMetalInputs[key] || {}
    const overridden = Number.isFinite(inputForMetal.purityPct) ? inputForMetal.purityPct : null
    const manifestPurity = byMetal[key]?.weightedAvgPurityPct
    const resolved = overridden !== null
      ? overridden
      : (Number.isFinite(manifestPurity) ? manifestPurity : null)
    if (resolved !== null) purityPctByMetal[key] = resolved
  }
  const previewByMetal = {}
  for (const metal of paidMetals) {
    const metalKey = String(metal).toLowerCase()
    const inputForMetal = perMetalInputs[metalKey] || {}
    const purityPct = Number.isFinite(inputForMetal.purityPct) ? inputForMetal.purityPct : null
    const metalValueUsd = Number.isFinite(inputForMetal.metalValueUsd) ? inputForMetal.metalValueUsd : null
    const manifestPurity = byMetal[metalKey]?.weightedAvgPurityPct
    const purityForTier = purityPct !== null
      ? purityPct
      : (Number.isFinite(manifestPurity) ? manifestPurity : null)
    const tier = resolveMetalRateTier(contract, metalKey, {
      grossToz: reportedGrossToz,
      purityPct: purityForTier,
      purityPctByMetal,
    })

    // "Credited" mirrors the contract data's vocabulary: `accountabilityPercent`
    // is the fraction of fine metal the partner credits us with on settle.
    const creditedFineToz = (purityPct !== null && reportedGrossToz !== null && tier)
      ? reportedGrossToz * (purityPct / 100) * (tier.accountabilityPercent / 100)
      : null

    // Missing/non-string feeBasis treated as default 'creditedFineToz' (matches validator's silent default).
    const feeBasis = tier && typeof tier.feeBasis === 'string' && tier.feeBasis.trim().length > 0
      ? tier.feeBasis
      : 'creditedFineToz'
    const refiningFeeBasis = tier ? feeBasis : null
    const refiningFeeMultiplier = tier
      ? (feeBasis === 'netWeightReceivedToz' ? reportedGrossToz : creditedFineToz)
      : null
    const refiningFeeUsd = (refiningFeeMultiplier !== null && tier && Number.isFinite(tier.fee))
      ? refiningFeeMultiplier * tier.fee
      : null

    const varianceVsManifestPct = (purityPct !== null && Number.isFinite(manifestPurity))
      ? purityPct - manifestPurity
      : null

    const minimumReturnToz = tier && Number.isFinite(tier.minimumReturnToz) ? tier.minimumReturnToz : null
    const belowMinimumReturn = (
      creditedFineToz !== null &&
      minimumReturnToz !== null &&
      creditedFineToz < minimumReturnToz
    )

    const hasSettlementOverride = purityPct !== null && metalValueUsd !== null
    const excludedFromContract = tier === null && !hasSettlementOverride

    previewByMetal[metalKey] = {
      purityPct,
      creditedFineToz,
      refiningFeeUsd,
      refiningFeeBasis,
      refiningFeeMultiplier,
      metalValueUsd,
      varianceVsManifestPct,
      minimumReturnToz,
      belowMinimumReturn,
      excludedFromContract,
    }
  }
  return previewByMetal
}

// ============================================================================
// Settlement-time impurity-charge predicate
// ----------------------------------------------------------------------------
// Stateless predicate over a settlement payload. Lives here ahead of the
// SLA-modifier dispatch so call sites read forward only. Also exported as
// public API — settlement-card consumers categorize ad-hoc fees through
// the same predicate.
// ============================================================================

/**
 * True when the settlement carries any adHocFees row categorized as an
 * impurity charge. Categorization is by `code === 'impurity_charge'` only
 * (not label-sniffing) so operator-typed free text can't accidentally
 * trip lot-has-impurity-charges SLA modifiers.
 *
 * @param {object|null|undefined} settlement
 * @returns {boolean}
 */
export const settlementHasImpurityCharges = (settlement) => {
  if (!settlement || !Array.isArray(settlement.adHocFees)) return false
  return settlement.adHocFees.some((row) => row && row.code === 'impurity_charge')
}

// ============================================================================
// SLA timing (max business days + modifier dispatch)
// ============================================================================

/**
 * Whether a paid metal counts toward the conservative settlement SLA max.
 * When `optInMap` is absent (e.g. at `received`), assume all opt-in-gated
 * metals might be selected so we surface the longest possible SLA.
 */
export const isMetalIncludedForSla = (contract, metal, optInMap) => {
  const metalKey = String(metal).toLowerCase()
  if (optInMap === undefined || optInMap === null) return true
  const hasOptInGate = Boolean(contract?.assayFee?.[metalKey]?.optInAtSalesOrder)
  if (!hasOptInGate) return true
  return optInMap[metalKey] === true
}

/**
 * Max `settlementBusinessDays` across included paid metals on the contract,
 * plus any matching contract-level `slaModifiers`.
 *
 * Modifier dispatch (split-by-knowability):
 *   - manifest-knowable conditions (metal-purity, lot-weight) fire whenever
 *     manifest data is present, regardless of whether `settlement` is.
 *   - settlement-time-only-knowable conditions (`hasImpurityCharges`) fire
 *     via {@link settlementHasImpurityCharges}; when `settlement` is absent
 *     the condition silently fails so the pre-transcription ETA stays
 *     honest (no "+10bd already, why?" surprise on `received`).
 *
 * AND semantics across all populated `when.*` keys within a single
 * modifier — every populated condition must match for the +N bump to
 * apply. OR semantics come from declaring multiple modifiers.
 *
 * Returns null when no SLA is defined on any qualifying tier (and no
 * modifier fires).
 */
export const resolveMaxSettlementBusinessDays = (contract, lot, optInMap, settlement) => {
  const paidMetals = Array.isArray(contract?.paidMetals) ? contract.paidMetals : []
  const tiers = Array.isArray(contract?.metalRates) ? contract.metalRates : []
  const manifestSummary = lot ? buildManifestSummaryForVariance(lot) : null
  // Route cross-metal tier resolution through the qualifying metal's manifest purity for SLA timing — otherwise Au would silently miss its tier (own purity isn't the qualifier).
  const purityPctByMetal = buildPurityPctByMetalForSla(paidMetals, manifestSummary)
  let maxDays = null
  for (const metal of paidMetals) {
    if (!isMetalIncludedForSla(contract, metal, optInMap)) continue
    const metalKey = String(metal).toLowerCase()
    const tierDimension = contractTierDimension(contract)
    const qualifyingMetalKey = (contract && contract.tierQualifyingMetal)
      ? String(contract.tierQualifyingMetal).toLowerCase()
      : metalKey
    const hasTierQualifier = manifestSummary && (
      tierDimension === 'weightToz'
        ? Number.isFinite(manifestSummary.grossWeightToz)
        : Number.isFinite(manifestSummary.byMetal[qualifyingMetalKey]?.weightedAvgPurityPct)
    )
    const matchedTier = manifestSummary
      ? resolveMetalRateTier(contract, metalKey, {
          grossToz: manifestSummary.grossWeightToz,
          purityPct: manifestSummary.byMetal[metalKey]?.weightedAvgPurityPct,
          purityPctByMetal,
        })
      : null
    const rowsForMetal = matchedTier
      ? [matchedTier]
      : (hasTierQualifier ? [] : tiers.filter((row) => row && String(row.metalType).toLowerCase() === metalKey))
    for (const row of rowsForMetal) {
      if (!Number.isFinite(row.settlementBusinessDays)) continue
      if (maxDays === null || row.settlementBusinessDays > maxDays) {
        maxDays = row.settlementBusinessDays
      }
    }
  }
  const modifierBump = computeSlaModifierBump(contract, manifestSummary, settlement)
  if (modifierBump > 0) {
    if (maxDays === null) return modifierBump
    return maxDays + modifierBump
  }
  return maxDays
}

const buildPurityPctByMetalForSla = (paidMetals, manifestSummary) => {
  if (!manifestSummary || !manifestSummary.byMetal) return {}
  const out = {}
  for (const metal of paidMetals) {
    const key = String(metal).toLowerCase()
    const value = manifestSummary.byMetal[key]?.weightedAvgPurityPct
    if (Number.isFinite(value)) out[key] = value
  }
  return out
}

const TOZ_PER_KG = 32.1507466 // inverse of 0.0311034768; matches our existing kg-toz constant

const matchesPurityCondition = (when, manifestSummary) => {
  if (!when || !manifestSummary) return true
  const hasPurityCondition = (when.purityPctMin !== undefined && when.purityPctMin !== null)
    || (when.purityPctMax !== undefined && when.purityPctMax !== null)
  if (!hasPurityCondition) return true
  const metalKey = when.metal && String(when.metal).toLowerCase()
  if (!metalKey) return false
  const manifestPurity = manifestSummary.byMetal?.[metalKey]?.weightedAvgPurityPct
  if (!Number.isFinite(manifestPurity)) return false
  if (when.purityPctMax !== undefined && when.purityPctMax !== null && !(manifestPurity <= when.purityPctMax)) return false
  if (when.purityPctMin !== undefined && when.purityPctMin !== null && !(manifestPurity >= when.purityPctMin)) return false
  return true
}

const matchesWeightCondition = (when, manifestSummary) => {
  if (!when) return true
  const hasKg = when.lotWeightKgMax !== undefined && when.lotWeightKgMax !== null
  const hasToz = when.lotWeightTozMax !== undefined && when.lotWeightTozMax !== null
  if (!hasKg && !hasToz) return true
  const grossToz = manifestSummary?.grossWeightToz
  if (!Number.isFinite(grossToz)) return false
  if (hasToz && !(grossToz <= when.lotWeightTozMax)) return false
  if (hasKg) {
    const grossKg = grossToz / TOZ_PER_KG
    if (!(grossKg <= when.lotWeightKgMax)) return false
  }
  return true
}

const matchesImpurityCondition = (when, settlement) => {
  if (!when || when.hasImpurityCharges !== true) return true
  // Settlement-time-only-knowable — default false until transcribed.
  return settlementHasImpurityCharges(settlement)
}

const matchesModifier = (modifier, manifestSummary, settlement) => {
  const when = modifier && modifier.when
  if (!when) return false
  return matchesPurityCondition(when, manifestSummary)
    && matchesWeightCondition(when, manifestSummary)
    && matchesImpurityCondition(when, settlement)
}

const computeSlaModifierBump = (contract, manifestSummary, settlement) => {
  const modifiers = Array.isArray(contract?.slaModifiers) ? contract.slaModifiers : []
  if (modifiers.length === 0) return 0
  let bump = 0
  for (const modifier of modifiers) {
    if (!modifier || !Number.isFinite(modifier.addBusinessDays)) continue
    if (matchesModifier(modifier, manifestSummary, settlement)) {
      bump += modifier.addBusinessDays
    }
  }
  return bump
}

// ============================================================================
// Settlement totals (lot-level USD aggregates)
// ============================================================================

/**
 * Aggregate per-metal preview + contract fees + ad-hoc fees into the
 * lot-level USD totals the dialog footer & card render. Treats missing
 * numerics as 0 so a partially-filled dialog still emits coherent totals.
 *
 *   grossMetalCashUsd    = sum(perMetal[*].metalValueUsd)
 *   totalRefiningFeeUsd  = sum(perMetal[*].refiningFeeUsd)
 *   totalAssayFeeUsd     = sum(contractFees.perMetalAssayFee[*].feeUsd
 *                              where row.optedIn !== false)
 *   totalOtherFeesUsd    = sum(resolveFeeAmount(contractFees.otherFees[*]))
 *                          (skips conditional-not-included rows; falls back
 *                           to flat `treatmentFeeUsd` for legacy persisted lots)
 *   totalContractFeesUsd = totalRefiningFeeUsd + totalAssayFeeUsd + totalOtherFeesUsd
 *   totalAdHocFeesUsd    = sum(adHocFees[*].amountUsd)   (signed — credits OK)
 *   netCashUsd           = grossMetalCashUsd − totalContractFeesUsd − totalAdHocFeesUsd
 *
 * `basisToz` in the per-toz fee context resolves to `reportedGrossToz` so a
 * fee like `kind: 'perToz'` scales against the inbound weight, matching the
 * customer contracts' `refiningFeePerUnit` semantics.
 */
export const computeSettlementTotals = ({contract, perMetal, contractFees, adHocFees, reportedGrossToz, qualifyingPurityPct} = {}) => {
  const perMetalMap = (perMetal && typeof perMetal === 'object') ? perMetal : {}
  let grossMetalCashUsd = 0
  let totalRefiningFeeUsd = 0
  for (const row of Object.values(perMetalMap)) {
    if (row?.excludedFromContract) continue
    grossMetalCashUsd += toFinite(row?.metalValueUsd)
    totalRefiningFeeUsd += toFinite(row?.refiningFeeUsd)
  }
  const perMetalAssayFee = (contractFees && contractFees.perMetalAssayFee) || {}
  let totalAssayFeeUsd = 0
  for (const row of Object.values(perMetalAssayFee)) {
    if (!row || row.optedIn === false) continue
    totalAssayFeeUsd += toFinite(row?.feeUsd)
  }
  // When 'contract' is passed, derive qualifyingPurityPct from 'perMetal[contract.tierQualifyingMetal].purityPct' so callers don't need to know the cross-metal threading. Explicit override wins. Flat 'perTozUsd' rows ignore this.
  const resolvedQualifyingPurityPct = Number.isFinite(qualifyingPurityPct)
    ? qualifyingPurityPct
    : deriveQualifyingPurityPctFromContract(contract, perMetalMap)
  const feeCtx = {
    basisToz: Number.isFinite(reportedGrossToz) ? reportedGrossToz : null,
    qualifyingPurityPct: Number.isFinite(resolvedQualifyingPurityPct) ? resolvedQualifyingPurityPct : null,
  }
  const otherFeeRows = Array.isArray(contractFees?.otherFees) ? contractFees.otherFees : []
  let totalOtherFeesUsd = 0
  for (const fee of otherFeeRows) {
    if (fee && fee.conditional === true && fee.included !== true) continue
    totalOtherFeesUsd += resolveFeeAmount(fee, feeCtx)
  }
  if (!otherFeeRows.length) {
    totalOtherFeesUsd += toFinite(contractFees?.treatmentFeeUsd)
  }
  const totalContractFeesUsd = totalRefiningFeeUsd + totalAssayFeeUsd + totalOtherFeesUsd
  const adHocRows = Array.isArray(adHocFees) ? adHocFees : []
  const totalAdHocFeesUsd = adHocRows.reduce((sum, row) => sum + toFinite(row?.amountUsd), 0)
  const netCashUsd = grossMetalCashUsd - totalContractFeesUsd - totalAdHocFeesUsd
  return {
    grossMetalCashUsd,
    totalRefiningFeeUsd,
    totalContractFeesUsd,
    totalAdHocFeesUsd,
    netCashUsd,
  }
}

const deriveQualifyingPurityPctFromContract = (contract, perMetalMap) => {
  if (!contract || !contract.tierQualifyingMetal) return null
  const key = String(contract.tierQualifyingMetal).toLowerCase()
  const row = perMetalMap[key]
  return row && Number.isFinite(row.purityPct) ? row.purityPct : null
}
